import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import useWebSocket from "./hooks/useWebSocket";

// Push notification utilities
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const autoSubscribePush = async (token) => {
  // Check if push is supported
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    console.log('Push notifications not supported');
    return;
  }

  // Check if already subscribed or permission denied
  if (Notification.permission === 'denied') {
    console.log('Notification permission denied');
    return;
  }

  try {
    // Check if already subscribed on server
    const statusRes = await axios.get(`${API}/push/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (statusRes.data.subscribed) {
      console.log('Already subscribed to push notifications');
      return;
    }

    // Request permission (will show browser prompt if not yet decided)
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission not granted');
      return;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key from server
    const vapidRes = await axios.get(`${API}/push/vapid-public-key`);
    const vapidPublicKey = vapidRes.data.public_key;

    if (!vapidPublicKey) {
      console.log('VAPID key not configured');
      return;
    }

    // Subscribe to push
    const pushSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });

    // Send subscription to server
    await axios.post(
      `${API}/push/subscribe`,
      { subscription: pushSubscription.toJSON() },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    console.log('Auto-subscribed to push notifications');
  } catch (error) {
    console.error('Auto-subscribe push error:', error);
  }
};

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CreateProductPage from "./pages/CreateProductPage";
import ProfilePage from "./pages/ProfilePage";
import FavoritesPage from "./pages/FavoritesPage";
import MessagesPage from "./pages/MessagesPage";
import ChatPage from "./pages/ChatPage";
import NotificationsPage from "./pages/NotificationsPage";
import SearchPage from "./pages/SearchPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ReviewPage from "./pages/ReviewPage";
import AdminProductsPage from "./pages/AdminProductsPage";
import MyPurchasesPage from "./pages/MyPurchasesPage";
import ActiveOffersPage from "./pages/ActiveOffersPage";
import ClientPanelPage from "./pages/ClientPanelPage";
import AdminReviewsPage from "./pages/AdminReviewsPage";
import MyRewardsPage from "./pages/MyRewardsPage";
import SectionPage from "./pages/SectionPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AdminBannersPage from "./pages/AdminBannersPage";
import AdminMessagingPage from "./pages/AdminMessagingPage";
import PromoPopup from "./components/PromoPopup";
import FloatingSupportWidget from "./components/FloatingSupportWidget";
import BroadcastModal from "./components/BroadcastModal";
import AdminAppConfigPage from "./pages/AdminAppConfigPage";
import InventoryPage from "./pages/InventoryPage";
import EditProductPage from "./pages/EditProductPage";
import CongratulationsModal from "./components/CongratulationsModal";
import LoadingScreen from "./components/LoadingScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

// WebSocket Context for real-time notifications
const WebSocketContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useWS = () => useContext(WebSocketContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [topbarEnabled, setTopbarEnabled] = useState(true);

  useEffect(() => {
    axios.get(`${API}/settings/topbar`).then((r) => setTopbarEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Re-read token from localStorage in case it was just set (after Google OAuth redirect)
    const currentToken = localStorage.getItem("token");
    if (currentToken && currentToken !== token) {
      setToken(currentToken);
    }

    const tokenToUse = currentToken || token;
    
    if (!tokenToUse) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${tokenToUse}` }
      });
      setUser(response.data);
      // Auto-subscribe to push notifications for existing sessions
      setTimeout(() => autoSubscribePush(tokenToUse), 2000);
    } catch (error) {
      // Only clear token if it's an authentication error (401)
      if (error.response?.status === 401) {
        setUser(null);
        localStorage.removeItem("token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkAuth();
    }, 50);
    return () => clearTimeout(timer);
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    // Auto-subscribe to push notifications
    setTimeout(() => autoSubscribePush(newToken), 1000);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    // Auto-subscribe to push notifications
    setTimeout(() => autoSubscribePush(newToken), 1000);
    return userData;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent('Clueer 🇵🇦')}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  // Function to complete Google login (called by AuthCallback)
  const completeGoogleLogin = (userData, jwtToken) => {
    localStorage.setItem("token", jwtToken);
    setToken(jwtToken);
    setUser(userData);
    // Auto-subscribe to push notifications
    setTimeout(() => autoSubscribePush(jwtToken), 1000);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, token, updateUser, completeGoogleLogin, setToken, setUser, topbarEnabled, setTopbarEnabled }}>
      {children}
    </AuthContext.Provider>
  );
};

// WebSocket Provider for real-time notifications
const WebSocketProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [bidUpdates, setBidUpdates] = useState({});
  const [newMessages, setNewMessages] = useState([]);
  const [congratsModal, setCongratsModal] = useState({ open: false, productTitle: null, amount: null, notificationId: null, productImage: null });
  const [broadcastModal, setBroadcastModal] = useState({ open: false, title: null, message: null });
  const [processedNotifications, setProcessedNotifications] = useState(new Set()); // Track processed notifications

  const handleNotification = useCallback((notification) => {
    // Prevent duplicate processing of the same notification
    if (processedNotifications.has(notification.notification_id)) {
      console.log("Notification already processed, skipping:", notification.notification_id);
      return;
    }
    
    // Mark as processed
    setProcessedNotifications(prev => new Set([...prev, notification.notification_id]));
    
    // Show toast notification
    const notifMessages = {
      bid: "Nueva oferta en tu producto",
      outbid: "Han superado tu oferta",
      comment: "Nuevo comentario",
      like: "A alguien le gustó tu producto",
      message: "Nuevo mensaje",
      sold: "¡Tu producto se ha vendido!",
      auction_won: "¡Ganaste la subasta!",
      payment_received: "¡Pago recibido!",
      payment_success: "¡Pago completado!",
      review: "Nueva reseña",
      offer_accepted: "¡Tu oferta fue aceptada!",
      counter_offer: "Nueva contraoferta recibida",
      purchase_confirmed: "¡Tu compra ha sido confirmada!"
    };

    const message = notification.message || notifMessages[notification.notif_type] || "Nueva notificación";

    // Difusiones del admin se muestran como popup emergente (reutilizando este mismo patrón)
    if (notification.notif_type === "promo") {
      setBroadcastModal({ open: true, title: notification.product_title || "Clueer", message });
      setUnreadCount(prev => prev + 1);
      return;
    }
    
    // Show congratulations modal for auction_won, offer_accepted, or purchase_confirmed
    if (notification.notif_type === "auction_won" || notification.notif_type === "offer_accepted" || notification.notif_type === "purchase_confirmed") {
      // Extract product title and amount from message
      // Message format examples:
      // "¡Tu oferta de $500 fue aceptada para iPhone 15!"
      // "¡Ganaste la subasta de MacBook Pro por $1500!"
      // "¡Tu compra de 'iPhone 15' ha sido confirmada!"
      // "¡Tu solicitud de 'Sony WH-1000XM5 Auriculares' ha sido aceptada!"
      let productTitle = notification.product_title || "tu producto";
      let amount = null;
      
      // Try to extract amount first (more reliable)
      const amountMatch = notification.message?.match(/\$([0-9,]+(?:\.[0-9]+)?)/);
      if (amountMatch) {
        amount = parseFloat(amountMatch[1].replace(",", ""));
      }
      
      // Only parse from message if product_title not provided
      if (!notification.product_title) {
        // Try multiple patterns to extract product title
        // Pattern 1: "de '[product]'" (quoted titles - most specific)
        let match = notification.message?.match(/de\s+'([^']+)'/i);
        if (match) {
          productTitle = match[1].trim();
        } else {
          // Pattern 2: "solicitud de '[product]'"
          match = notification.message?.match(/solicitud de\s+'([^']+)'/i);
          if (match) {
            productTitle = match[1].trim();
          } else {
            // Pattern 3: "para [product]!" or "para [product]"
            match = notification.message?.match(/para\s+(.+?)(?:!|$)/i);
            if (match) {
              productTitle = match[1].trim();
            } else {
              // Pattern 4: "de [product] por" 
              match = notification.message?.match(/de\s+(.+?)\s+por/i);
              if (match) {
                productTitle = match[1].trim();
              }
            }
          }
        }
      }
      
      console.log("CongratulationsModal triggered:", { notif_type: notification.notif_type, productTitle, amount, originalMessage: notification.message });
      
      setCongratsModal({
        open: true,
        productTitle,
        amount,
        notificationId: notification.notification_id
      });
    } else {
      // Regular toast notification
      toast.info(message, {
        duration: 5000,
        action: notification.product_id ? {
          label: "Ver",
          onClick: () => window.location.href = notification.action_url || `/product/${notification.product_id}`
        } : undefined
      });
    }

    // Update unread count
    setUnreadCount(prev => prev + 1);
  }, []);

  const handleBidUpdate = useCallback((productId, bidData) => {
    setBidUpdates(prev => ({
      ...prev,
      [productId]: bidData
    }));
  }, []);

  const handleNewMessage = useCallback((messageData) => {
    setNewMessages(prev => [...prev, messageData]);
    
    // Show toast for new message
    toast.info(`Nuevo mensaje de ${messageData.sender_name}`, {
      duration: 4000,
      action: {
        label: "Ver",
        onClick: () => window.location.href = `/chat/${messageData.sender_id}`
      }
    });
  }, []);

  const handleCartUpdate = useCallback((data) => {
    // Trigger a custom event that the Header can listen to
    window.dispatchEvent(new CustomEvent('cartUpdate', { detail: data }));
    
    // Show toast notification
    if (data.action === 'removed' || data.action === 'offer_rejected') {
      toast.info("Tu carrito ha sido actualizado", {
        duration: 3000,
        action: {
          label: "Ver",
          onClick: () => window.location.href = '/my-purchases'
        }
      });
    }
  }, []);

  const { isConnected, subscribeToProduct, disconnect } = useWebSocket(
    user ? token : null,
    handleNotification,
    handleBidUpdate,
    handleNewMessage,
    handleCartUpdate
  );

  // Polling for unshown win notifications (backup for WebSocket)
  useEffect(() => {
    if (!token || !user) return;
    
    const checkWinNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        // Find any recent unprocessed win notifications
        const recentWins = (response.data || []).filter(n => 
          (n.type === "auction_won" || n.type === "offer_accepted" || n.type === "purchase_confirmed") &&
          !n.read &&
          !processedNotifications.has(n.notification_id) && // Check if already processed
          // Only show if created in the last 5 minutes
          new Date(n.created_at) > new Date(Date.now() - 5 * 60 * 1000)
        );
        
        if (recentWins.length > 0 && !congratsModal.open) {
          const win = recentWins[0];
          
          // Mark as processed immediately to prevent duplicates
          setProcessedNotifications(prev => new Set([...prev, win.notification_id]));
          
          // Use product_title from notification if available
          let productTitle = win.product_title || "tu producto";
          let amount = null;
          let productImage = win.product_image || null;
          
          const amountMatch = win.message?.match(/\$([0-9,]+(?:\.[0-9]+)?)/);
          if (amountMatch) {
            amount = parseFloat(amountMatch[1].replace(",", ""));
          }
          
          // Only parse from message if product_title not provided
          if (!win.product_title) {
            // Try multiple patterns to extract product title
            // Pattern 1: "de '[product]'" (quoted titles - most specific)
            let match = win.message?.match(/de\s+'([^']+)'/i);
            if (match) {
              productTitle = match[1].trim();
            } else {
              // Pattern 2: "para [product]!" or "para [product]"
              match = win.message?.match(/para\s+(.+?)(?:!|$)/i);
              if (match) {
                productTitle = match[1].trim();
              } else {
                // Pattern 3: "de [product] por" 
                match = win.message?.match(/de\s+(.+?)\s+por/i);
                if (match) {
                  productTitle = match[1].trim();
                }
              }
            }
          }
          
          // Get product image from API only if not in notification
          if (!productImage && win.product_id) {
            try {
              const productRes = await axios.get(`${API}/products/${win.product_id}`);
              productImage = productRes.data?.image_url;
              if (!productTitle || productTitle === "tu producto") {
                productTitle = productRes.data?.title || productTitle;
              }
            } catch (e) {
              console.log("Could not fetch product details for modal");
            }
          }
          
          console.log("Showing win modal from polling:", { type: win.type, productTitle, amount, notificationId: win.notification_id, productImage });
          setCongratsModal({
            open: true,
            productTitle,
            amount,
            notificationId: win.notification_id,
            productImage
          });
        }
      } catch (error) {
        console.error("Error checking win notifications:", error);
      }
    };
    
    // Check immediately and then every 10 seconds
    checkWinNotifications();
    const interval = setInterval(checkWinNotifications, 10000);
    
    return () => clearInterval(interval);
  }, [token, user, congratsModal.open, processedNotifications]);

  const clearBidUpdate = useCallback((productId) => {
    setBidUpdates(prev => {
      const newUpdates = { ...prev };
      delete newUpdates[productId];
      return newUpdates;
    });
  }, []);

  const clearNewMessages = useCallback(() => {
    setNewMessages([]);
  }, []);

  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const closeCongratsModal = useCallback(async () => {
    // Mark the notification as read when closing the modal
    if (congratsModal.notificationId && token) {
      try {
        await axios.post(`${API}/notifications/${congratsModal.notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
    setCongratsModal({ open: false, productTitle: null, amount: null, notificationId: null, productImage: null });
  }, [congratsModal.notificationId, token]);

  const closeBroadcastModal = useCallback(() => {
    setBroadcastModal({ open: false, title: null, message: null });
  }, []);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      subscribeToProduct,
      disconnect,
      unreadCount,
      resetUnreadCount,
      bidUpdates,
      clearBidUpdate,
      newMessages,
      clearNewMessages,
      congratsModal,
      closeCongratsModal,
      broadcastModal,
      closeBroadcastModal
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeGoogleLogin } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          // Check if we got a valid response with token
          if (response.data && response.data.token) {
            // Use the new completeGoogleLogin function to set state directly
            completeGoogleLogin(
              {
                user_id: response.data.user_id,
                email: response.data.email,
                name: response.data.name,
                picture: response.data.picture,
                is_admin: response.data.is_admin
              },
              response.data.token
            );
            
            toast.success(`¡Bienvenido, ${response.data.name}!`);
            
            // Clean URL and navigate without reload
            window.history.replaceState(null, '', '/');
            navigate('/', { replace: true });
          } else {
            console.error("Invalid response from /auth/session:", response.data);
            toast.error("Error: Respuesta inválida del servidor");
            navigate("/login", { replace: true });
          }
        } catch (error) {
          console.error("Session exchange error:", error.response?.data || error.message);
          let errorMsg = "Error al iniciar sesión con Google";
          
          if (error.response?.data?.detail) {
            if (error.response.data.detail.includes("Invalid session")) {
              errorMsg = "La sesión de Google ha expirado. Por favor intenta de nuevo.";
            } else {
              errorMsg = error.response.data.detail;
            }
          }
          
          toast.error(errorMsg);
          navigate("/login", { replace: true });
        }
      } else {
        // No session_id in hash, just go home
        navigate("/", { replace: true });
      }
    };

    processSession();
  }, [location, navigate, completeGoogleLogin]);

  return <LoadingScreen message="Iniciando sesión..." />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Verificando sesión..." />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component - Solo administradores
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Verificando sesión..." />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id during render (NOT in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/section/:sectionType" element={<SectionPage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      <Route path="/profile/:userId" element={<ProfilePage />} />
      <Route
        path="/create"
        element={
          <AdminRoute>
            <CreateProductPage />
          </AdminRoute>
        }
      />
      <Route
        path="/edit/:productId"
        element={
          <AdminRoute>
            <EditProductPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <AdminRoute>
            <AdminProductsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <AdminRoute>
            <InventoryPage />
          </AdminRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:chatUserId"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:userId"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review/:productId"
        element={
          <ProtectedRoute>
            <ReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-purchases"
        element={
          <ProtectedRoute>
            <MyPurchasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-rewards"
        element={
          <ProtectedRoute>
            <MyRewardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/active-offers"
        element={
          <AdminRoute>
            <ActiveOffersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <AdminRoute>
            <ClientPanelPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/reviews"
        element={
          <AdminRoute>
            <AdminReviewsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/banners"
        element={
          <AdminRoute>
            <AdminBannersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/app-config"
        element={
          <AdminRoute>
            <AdminAppConfigPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/messaging"
        element={
          <AdminRoute>
            <AdminMessagingPage />
          </AdminRoute>
        }
      />
    </Routes>
  );
}

// Modal de felicitaciones global
const GlobalCongratsModal = () => {
  const wsContext = useWS();
  
  // Safety check - if context is null or modal is not set, don't render
  if (!wsContext || !wsContext.congratsModal) return null;
  
  const { congratsModal, closeCongratsModal } = wsContext;
  
  return (
    <CongratulationsModal
      open={congratsModal.open}
      onClose={closeCongratsModal}
      productTitle={congratsModal.productTitle}
      amount={congratsModal.amount}
      productImage={congratsModal.productImage}
    />
  );
};

// Modal de difusión/anuncio del admin, global
const GlobalBroadcastModal = () => {
  const wsContext = useWS();

  if (!wsContext || !wsContext.broadcastModal) return null;

  const { broadcastModal, closeBroadcastModal } = wsContext;

  return (
    <BroadcastModal
      open={broadcastModal.open}
      onClose={closeBroadcastModal}
      title={broadcastModal.title}
      message={broadcastModal.message}
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <Toaster 
            position="top-right" 
            richColors 
            duration={3000}
            toastOptions={{
              style: { marginTop: '60px' }
            }}
          />
          <GlobalCongratsModal />
          <GlobalBroadcastModal />
          <PromoPopup />
          <FloatingSupportWidget />
          <AppRouter />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
